/**
 * Client-side normalization + stable hashing for assistant context.
 *
 * Mirrors azfApi/ai/context/normalize.js. The two must produce the same hash
 * for the same page state: the client sends its hash, the server recomputes
 * it, and a divergence means the two disagree about what was sent.
 *
 * Purely structural — no financial values pass through here, and nothing this
 * file produces is trusted by the server on its own.
 */

import {
  ASSISTANT_CONTEXT_SCHEMA,
  AssistantContextEnvelope,
  AssistantContextSnapshot,
  AssistantPageState,
} from './assistantContextTypes';

/** Order-stable serialization — key order must not change a hash. */
export function stableStringify(value: unknown): string {
  if (value === null || value === undefined) return 'null';
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const keys = Object.keys(obj).sort();
    return `{${keys.map(k => `${JSON.stringify(k)}:${stableStringify(obj[k])}`).join(',')}}`;
  }
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : 'null';
  return JSON.stringify(value);
}

/**
 * FNV-1a 64-bit (as two 32-bit halves), hex-encoded.
 *
 * Not SHA-256: WebCrypto's digest is async, and the context hash has to be
 * available synchronously during render for the chip and for request assembly.
 * This hash is an EQUALITY/IDENTITY marker for snapshots, never a security
 * boundary — the server recomputes its own SHA-256 over the normalized
 * envelope and authorizes every id independently.
 */
export function hashSnapshot(value: unknown): string {
  const input = stableStringify(value);
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  for (let i = 0; i < input.length; i++) {
    const c = input.charCodeAt(i);
    h1 ^= c;
    h1 = Math.imul(h1, 0x01000193) >>> 0;
    h2 = Math.imul(h2 ^ c, 0x85ebca6b) >>> 0;
  }
  return `fnv1a:${h1.toString(16).padStart(8, '0')}${h2.toString(16).padStart(8, '0')}`;
}

function toInt(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : parseInt(String(value), 10);
  return Number.isInteger(n) ? n : null;
}

/** A calendar date, or null. Strict on purpose — see the server's toIsoDate. */
function toIsoDate(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  const text = String(value).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return null;
  const parsed = Date.parse(`${text}T00:00:00Z`);
  if (!Number.isFinite(parsed)) return null;
  return new Date(parsed).toISOString().slice(0, 10) === text ? text : null;
}

/** Selections identified by a business date rather than a row id. */
const DATE_SELECTION_KINDS = ['journal_day'];

/**
 * Turn a page adapter's published state into a normalized envelope + hash.
 * Returns null when the state is not yet usable (no org or no property), so a
 * half-mounted page never publishes a partial context.
 */
export function normalizePageState(state: AssistantPageState | null): AssistantContextSnapshot | null {
  if (!state) return null;

  const orgId = toInt(state.orgId);
  if (orgId == null) return null;

  // Array.from rather than a spread: the build targets es5, where spreading a
  // Set needs downlevelIteration.
  const propertyIds = Array.from(new Set((state.propertyIds || []).map(toInt).filter((n): n is number => n != null)))
    .sort((a, b) => a - b);
  if (propertyIds.length === 0) return null;

  const filters: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(state.filters || {})) {
    if (value === null || value === undefined) continue;
    if (typeof value === 'object') continue;  // page data, not context
    filters[key] = typeof value === 'number' ? value : String(value);
  }

  let selection: AssistantContextEnvelope['selection'] = null;
  if (state.selection) {
    if (DATE_SELECTION_KINDS.indexOf(state.selection.kind) !== -1) {
      const date = toIsoDate(state.selection.date);
      selection = date ? { kind: state.selection.kind, date, label: state.selection.label ?? null } : null;
    } else {
      const id = toInt(state.selection.id);
      selection = id != null ? { kind: state.selection.kind, id, label: state.selection.label ?? null } : null;
    }
  }

  const dataRefs = (state.dataRefs || [])
    .map(ref => ({ kind: ref.kind, id: toInt(ref.id) }))
    .filter((ref): ref is { kind: AssistantContextEnvelope['dataRefs'][number]['kind']; id: number } => ref.id != null)
    .sort((a, b) => (a.kind === b.kind ? a.id - b.id : a.kind.localeCompare(b.kind)));

  const envelope: AssistantContextEnvelope = {
    schema: ASSISTANT_CONTEXT_SCHEMA,
    pageKey: state.pageKey,
    capturedAt: new Date().toISOString(),
    scope: { orgId, propertyIds },
    view: {
      route: state.view?.route ?? null,
      mode: state.view?.mode ?? null,
      versionId: toInt(state.view?.versionId),
      year: toInt(state.view?.year),
      activeMonth: toInt(state.view?.activeMonth),
      startDate: toIsoDate(state.view?.startDate),
      endDate: toIsoDate(state.view?.endDate),
    },
    filters,
    selection,
    dataRefs,
    capabilities: Array.from(new Set(state.capabilities || [])).sort(),
  };

  // capturedAt is excluded from the hash on purpose: it changes on every
  // render and would make an identical page state look like a new snapshot.
  const { capturedAt, ...hashable } = envelope;
  return {
    envelope,
    hash: hashSnapshot(hashable),
    label: (state.labelParts || []).filter(Boolean).join(' · '),
  };
}
